/**
 * Diagrama unifilar (Grupo B, até dois inversores string) para Gestão de Clientes.
 * O PDF é desenhado em vetor (jsPDF), folha A4 no modelo vertical (rede, padrão, quadro CA, gerador e inversor).
 */

import { montarDiagramaBlocos, montarFolhaUnifilar } from './projetoUnifilarFolha.js'

const DISJUNTORES = [6, 10, 16, 20, 25, 32, 40, 50, 63, 70, 80, 100, 125, 150, 200]

/** Ampacidade de condutores, escala métrica NBR 70 °C. */
const SECOES_NBR_70 = [
  [0.5, 6],
  [0.75, 10],
  [1, 12],
  [1.5, 15.5],
  [2.5, 21],
  [4, 28],
  [6, 36],
  [10, 50],
  [16, 68],
  [25, 89],
  [35, 111],
  [50, 134],
  [70, 171],
  [95, 207],
  [120, 239]
]

export const DISJUNTORES_TRIFASICO_PADRAO = [70, 80, 90, 100, 110, 115, 120, 200, 300]

export function opcoesDisjuntorPadrao(padrao) {
  if (padrao === 'bifasico') return [63]
  if (padrao === 'trifasico') return DISJUNTORES_TRIFASICO_PADRAO
  return null
}

export function normalizarDisjuntorPadrao(padrao, atual) {
  const opcoes = opcoesDisjuntorPadrao(padrao)
  if (!opcoes) return String(atual ?? '').trim()
  const n = num(atual)
  if (opcoes.includes(n)) return String(n)
  return String(opcoes[0])
}

/** Seção da faixa que começa na corrente da linha e vai até a linha seguinte, exclusive. 50 A a 67 A = 10 mm²; 68 A = 16 mm². */
export function secaoPorCorrente(amperes) {
  const i = num(amperes)
  if (i <= 0) return null
  let escolhida = SECOES_NBR_70[0][0]
  for (const [secao, capacidade] of SECOES_NBR_70) {
    if (capacidade <= i) escolhida = secao
    else break
  }
  return escolhida
}

const textoSecao = (mm) => String(mm).replace('.', ',')

/** Entre outras cargas e o Q. proteção CA a bitola não fica abaixo de 6 mm². */
const SECAO_MINIMA_QUADRO_CA = 6

/** Bitola CA no formato 2#10 (10)mm² (0,6/1 kV), pela corrente do disjuntor. */
export function textoBitolaCa(padrao, amperes, minima = 0) {
  const calculada = secaoPorCorrente(amperes)
  const secao = calculada == null ? null : Math.max(calculada, minima)
  const condutores = padrao === 'trifasico' ? 4 : 2
  if (!secao) return padrao === 'trifasico' ? '4# (0,6/1 kV)' : '2# (0,6/1 kV)'
  const texto = textoSecao(secao)
  return `${condutores}#${texto} (${texto})mm² (0,6/1 kV)`
}

const num = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'))
  return Number.isFinite(n) ? n : 0
}

const inteiro = (v) => {
  const n = parseInt(v, 10)
  return Number.isFinite(n) ? n : 0
}

const textoOu = (v, fallback = '—') => {
  const s = String(v ?? '').trim()
  return s || fallback
}

export function rotuloPadrao(padrao) {
  if (padrao === 'trifasico') return 'Trifásico 220/380 V'
  if (padrao === 'bifasico') return 'Bifásico 220 V'
  return 'Monofásico 220 V'
}

export function fasesDoPadrao(padrao) {
  if (padrao === 'trifasico') return 3
  if (padrao === 'bifasico') return 2
  return 1
}

/** Corrente de saída do inversor (A), arredondada para cima. Monofásico e bifásico em 220 V; trifásico em 220/380 V. */
export function correnteInversor(potenciaKw, padrao) {
  const w = num(potenciaKw) * 1000
  if (w <= 0) return null
  const i = padrao === 'trifasico' ? w / (Math.sqrt(3) * 220) : w / 220
  return Math.max(1, Math.ceil(i))
}

/** Disjuntor comercial imediatamente acima de 1,25 × a corrente informada. */
export function disjuntorComercial(corrente) {
  const i = num(corrente)
  if (i <= 0) return null
  const alvo = i * 1.25
  return DISJUNTORES.find((s) => s >= alvo) || DISJUNTORES[DISJUNTORES.length - 1]
}

/** Até 14 módulos ficam em uma string; acima disso, grupos próximos de 12. */
export function sugerirStrings(qtdModulos) {
  const qtd = inteiro(qtdModulos)
  if (qtd <= 1) return 1
  if (qtd <= 14) return 1
  return Math.ceil(qtd / 12)
}

export function distribuirStrings(qtdModulos, qtdStrings) {
  const qtd = Math.max(0, inteiro(qtdModulos))
  const s = Math.max(1, inteiro(qtdStrings) || 1)
  if (!qtd) return Array.from({ length: s }, () => 0)
  const base = Math.floor(qtd / s)
  const resto = qtd % s
  return Array.from({ length: s }, (_, i) => base + (i < resto ? 1 : 0))
}

export function resumoUnifilar(form = {}) {
  const qtd = Math.max(0, inteiro(form.qtdModulos))
  const wp = num(form.potenciaModuloW)
  const strings = Math.max(1, inteiro(form.qtdStrings) || 1)
  const dist = distribuirStrings(qtd, strings)
  const kwp = qtd > 0 && wp > 0 ? (qtd * wp) / 1000 : 0
  return { qtd, wp, strings, dist, kwp }
}

export function formularioInicialUnifilar(cliente = {}) {
  const endereco = [cliente.logradouro, cliente.numero, cliente.bairro].filter(Boolean).join(', ')
  return {
    _clienteId: cliente.id || null,
    cliente: cliente.nome_completo || '',
    endereco,
    cep: cliente.cep || '',
    cidade: cliente.cidade || '',
    uf: cliente.estado || '',
    padrao: 'bifasico',
    disjuntorPadrao: '63',
    modulos: [],
    marcaModulo: '',
    modeloModulo: '',
    potenciaModuloW: '',
    qtdModulos: '',
    qtdStrings: '1',
    inversores: [inversorVazio()],
    marcaInversor: '',
    modeloInversor: '',
    potenciaInversorKw: '',
    correnteInversorA: '',
    disjuntorA: '',
    caboRamal: '',
    caboCa: '',
    caboCc: '',
    vmp: '',
    imp: '',
    responsavel: '',
    registro: '',
    avisos: []
  }
}

const CHAVE_MEMORIA_FORMULARIO = 'tecsol-projeto-unifilar-formulario'
const CAMPOS_MEMORIA_FORMULARIO = [
  'padrao',
  'disjuntorPadrao',
  'disjuntorA',
  'marcaModulo',
  'modeloModulo',
  'potenciaModuloW',
  'qtdModulos',
  'qtdStrings',
  'vmp',
  'imp'
]

const valorPreenchido = (valor) => {
  if (Array.isArray(valor)) return valor.length > 0
  return valor !== undefined && valor !== null && String(valor).trim() !== ''
}

export function salvarMemoriaFormularioUnifilar(form = {}) {
  try {
    const memoria = {}
    CAMPOS_MEMORIA_FORMULARIO.forEach((campo) => {
      memoria[campo] = form[campo] ?? ''
    })
    memoria.inversores = (form.inversores || []).map((inv) => ({
      marca: inv.marca || '',
      modelo: inv.modelo || '',
      potenciaKw: inv.potenciaKw || '',
      correnteA: inv.correnteA || '',
      disjuntorA: inv.disjuntorA || '',
      ligacao: inv.ligacao || ''
    }))
    memoria.modulos = Array.isArray(form.modulos) ? form.modulos : []
    localStorage.setItem(CHAVE_MEMORIA_FORMULARIO, JSON.stringify(memoria))
  } catch (error) {
    console.warn('Não foi possível salvar o formulário do projeto.', error)
  }
}

export function lerMemoriaFormularioUnifilar() {
  try {
    const bruto = localStorage.getItem(CHAVE_MEMORIA_FORMULARIO)
    if (!bruto) return null
    const memoria = JSON.parse(bruto)
    return memoria && typeof memoria === 'object' ? memoria : null
  } catch {
    return null
  }
}

export function aplicarMemoriaFormularioUnifilar(form, memoria = lerMemoriaFormularioUnifilar()) {
  if (!memoria) return form
  const next = { ...form }
  CAMPOS_MEMORIA_FORMULARIO.forEach((campo) => {
    if (valorPreenchido(memoria[campo])) next[campo] = memoria[campo]
  })
  if (valorPreenchido(memoria.inversores)) {
    next.inversores = memoria.inversores.map((inv) => ({ ...inversorVazio(), ...inv }))
  }
  if (valorPreenchido(memoria.modulos)) next.modulos = memoria.modulos
  return espelharInversorPrincipal(next)
}

/** Mantém Vmp, Imp, corrente e os outros ajustes depois que o kit do cliente entra. */
export function preservarCamposSalvosUnifilar(preenchidoPeloKit, anterior = {}) {
  const next = { ...preenchidoPeloKit }
  if (valorPreenchido(anterior.vmp)) next.vmp = anterior.vmp
  if (valorPreenchido(anterior.imp)) next.imp = anterior.imp
  if (valorPreenchido(anterior.qtdStrings)) next.qtdStrings = anterior.qtdStrings
  if (valorPreenchido(anterior.padrao)) next.padrao = anterior.padrao
  if (valorPreenchido(anterior.disjuntorPadrao)) {
    next.disjuntorPadrao = normalizarDisjuntorPadrao(next.padrao, anterior.disjuntorPadrao)
  }
  if (valorPreenchido(anterior.disjuntorA)) next.disjuntorA = anterior.disjuntorA
  const salvos = Array.isArray(anterior.inversores) ? anterior.inversores : []
  next.inversores = (next.inversores || []).map((inv, indice) => {
    const salvo = salvos[indice]
    if (!salvo) return inv
    return {
      ...inv,
      ligacao: valorPreenchido(salvo.ligacao) ? salvo.ligacao : inv.ligacao,
      correnteA: valorPreenchido(salvo.correnteA) ? salvo.correnteA : inv.correnteA,
      disjuntorA: valorPreenchido(salvo.disjuntorA) ? salvo.disjuntorA : inv.disjuntorA
    }
  })
  return espelharInversorPrincipal(next)
}

const linhaKitPreenchida = (linha = {}) => Boolean(
  String(linha.marca || '').trim() ||
  String(linha.modelo || '').trim() ||
  (linha.potencia !== '' && linha.potencia != null) ||
  (linha.quantidade !== '' && linha.quantidade != null)
)

const qtdLinhaKit = (linha = {}) => {
  const n = inteiro(linha.quantidade)
  return n > 0 ? n : 1
}

const inversorVazio = () => ({
  marca: '',
  modelo: '',
  potenciaKw: '',
  correnteA: '',
  disjuntorA: '',
  ligacao: 'bifasico'
})

const ligacaoDoInversor = (inv, reserva = 'bifasico') => inv.ligacao || reserva || 'bifasico'

const aplicarCorrenteNoInversor = (inv) => {
  const corrente = correnteInversor(inv.potenciaKw, ligacaoDoInversor(inv))
  if (!corrente) return { ...inv, ligacao: ligacaoDoInversor(inv) }
  return {
    ...inv,
    ligacao: ligacaoDoInversor(inv),
    correnteA: String(corrente),
    disjuntorA: String(disjuntorComercial(corrente))
  }
}

const disjuntorGeral = (inversores) => {
  const soma = inversores.reduce((total, inv) => total + num(inv.correnteA), 0)
  if (soma <= 0) return ''
  return String(disjuntorComercial(soma))
}

const espelharInversorPrincipal = (form) => {
  const inv = (form.inversores && form.inversores[0]) || inversorVazio()
  return {
    ...form,
    marcaInversor: inv.marca || '',
    modeloInversor: inv.modelo || '',
    potenciaInversorKw: inv.potenciaKw || '',
    correnteInversorA: inv.correnteA || ''
  }
}

/** Corrente do disjuntor desenhado no Q. proteção CA. Com dois valores, vale o maior. */
export function ampereQuadroCa(dados = {}) {
  const amps = inversoresDoFormulario(dados).map((inv) => num(inv.disjuntorA)).filter((n) => n > 0)
  if (amps.length) return Math.max(...amps)
  return num(dados.disjuntorA)
}

export function inversoresDoFormulario(form = {}) {
  if (Array.isArray(form.inversores) && form.inversores.length) {
    return form.inversores.slice(0, 2).map((inv) => ({
      ...inv,
      ligacao: inv.ligacao || form.padrao || 'bifasico'
    }))
  }
  if (form.marcaInversor || form.modeloInversor || form.potenciaInversorKw || form.correnteInversorA) {
    return [{
      marca: form.marcaInversor || '',
      modelo: form.modeloInversor || '',
      potenciaKw: form.potenciaInversorKw || '',
      correnteA: form.correnteInversorA || '',
      disjuntorA: form.disjuntorA || '',
      ligacao: form.padrao || 'bifasico'
    }]
  }
  return [inversorVazio()]
}

const expandirInversoresDoKit = (linhas, padrao) => {
  const lista = []
  linhas.forEach((linha) => {
    const qtd = qtdLinhaKit(linha)
    for (let i = 0; i < qtd; i += 1) {
      lista.push(aplicarCorrenteNoInversor({
        marca: linha.marca || '',
        modelo: linha.modelo || '',
        potenciaKw: linha.potencia != null && linha.potencia !== '' ? String(linha.potencia) : '',
        correnteA: '',
        disjuntorA: '',
        ligacao: linha.ligacao || padrao || 'bifasico'
      }))
    }
  })
  return lista
}

export function preencherComKit(form, kit = {}) {
  const next = { ...form, avisos: [] }
  const inversores = (kit.inversores || []).filter(linhaKitPreenchida)
  const modulos = (kit.modulos || []).filter(linhaKitPreenchida)
  const mod = modulos[0] || {}

  next.modulos = modulos.map((linha) => ({
    modelo: linha.modelo || '',
    potenciaW: linha.potencia != null && linha.potencia !== '' ? String(linha.potencia) : '',
    quantidade: String(qtdLinhaKit(linha))
  }))
  if (modulos.length === 1) {
    next.marcaModulo = mod.marca || ''
    next.modeloModulo = mod.modelo || ''
    if (mod.potencia != null && mod.potencia !== '') next.potenciaModuloW = String(mod.potencia)
    if (mod.quantidade != null && mod.quantidade !== '') next.qtdModulos = String(mod.quantidade)
  } else if (modulos.length > 1) {
    const qtd = modulos.reduce((soma, linha) => soma + qtdLinhaKit(linha), 0)
    next.marcaModulo = mod.marca || ''
    next.modeloModulo = modulos.map((linha) => linha.modelo).filter(Boolean).join(' / ')
    next.potenciaModuloW = mod.potencia != null && mod.potencia !== '' ? String(mod.potencia) : ''
    next.qtdModulos = String(qtd)
    if (modulos.length > 2) {
      next.avisos.push(`O kit tem ${modulos.length} modelos de módulo. Os PDFs desenham os dois primeiros.`)
    }
  }

  if (next.qtdModulos) next.qtdStrings = String(sugerirStrings(next.qtdModulos))

  const lista = expandirInversoresDoKit(inversores, next.padrao)
  const desenhados = (lista.length ? lista : [inversorVazio()]).slice(0, 2)
  if (lista.length > 2) {
    next.avisos.push(`O kit tem ${lista.length} inversores. Os PDFs desenham os dois primeiros.`)
  }
  next.inversores = desenhados
  const preenchido = espelharInversorPrincipal(next)
  preenchido.disjuntorA = desenhados.length > 1
    ? (disjuntorGeral(desenhados) || preenchido.disjuntorA)
    : (desenhados[0].disjuntorA || '')
  preenchido.disjuntorPadrao = normalizarDisjuntorPadrao(
    preenchido.padrao,
    preenchido.disjuntorPadrao || preenchido.disjuntorA
  )
  return preenchido
}

export function atualizarInversorUnifilar(form, index, campo, valor) {
  const inversores = inversoresDoFormulario(form).map((inv) => ({ ...inv }))
  if (!inversores[index]) return form
  inversores[index] = { ...inversores[index], [campo]: valor }
  if (campo === 'potenciaKw' || campo === 'ligacao') {
    inversores[index] = aplicarCorrenteNoInversor(inversores[index])
  }
  const next = espelharInversorPrincipal({ ...form, inversores })
  if (inversores.length > 1 && (campo === 'potenciaKw' || campo === 'correnteA' || campo === 'ligacao')) {
    next.disjuntorA = disjuntorGeral(inversores) || next.disjuntorA
  } else if (inversores.length === 1) {
    next.disjuntorA = inversores[0].disjuntorA || ''
  }
  return next
}

export function aplicarCampoUnifilar(form, campo, valor) {
  if (campo === 'padrao') {
    const inversores = inversoresDoFormulario(form).map((inv) => ({
      ...inv,
      ligacao: inv.ligacao || form.padrao || 'bifasico'
    }))
    const next = espelharInversorPrincipal({ ...form, padrao: valor, inversores })
    next.disjuntorPadrao = normalizarDisjuntorPadrao(valor, next.disjuntorPadrao)
    return next
  }
  const next = { ...form, [campo]: valor }
  if (campo === 'qtdModulos') {
    next.qtdStrings = String(sugerirStrings(valor))
  }
  return next
}

export function nomeArquivoUnifilar(cliente, tipo) {
  const prefixo = tipo === 'blocos' ? 'Diagrama_blocos' : 'Unifilar'
  const base = String(cliente || 'cliente')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 60) || 'cliente'
  return `${prefixo}_${base}.pdf`
}

const fmt = (n, casas = 2) => (Number(n) || 0).toFixed(casas).replace('.', ',')

export function textoPreviaUnifilar(form) {
  const resumo = resumoUnifilar(form)
  if (!resumo.qtd) return 'A quantidade de módulos define as strings e a potência do arranjo.'
  const grupos = resumo.dist.slice(0, 6).join(' + ')
  const reticencias = resumo.dist.length > 6 ? '…' : ''
  const kwp = resumo.kwp > 0 ? ` · ${fmt(resumo.kwp)} kWp` : ''
  return `${resumo.strings} string(s): ${grupos} módulos${reticencias}${kwp}`
}

function dadosDoPdf(dados = {}) {
  const amperePadrao = dados.disjuntorPadrao || dados.disjuntorA
  const bitolaPadrao = textoBitolaCa(dados.padrao, amperePadrao)
  const bitolaQuadro = textoBitolaCa(dados.padrao, ampereQuadroCa(dados), SECAO_MINIMA_QUADRO_CA)
  return {
    ...dados,
    caboRamal: bitolaPadrao,
    caboCa: bitolaPadrao,
    caboQuadro: bitolaQuadro
  }
}

export function montarPdfUnifilar(dados = {}) {
  return montarFolhaUnifilar(dadosDoPdf(dados))
}

export function baixarPdfUnifilar(dados) {
  const preparado = dadosDoPdf(dados)
  montarFolhaUnifilar(preparado).save(nomeArquivoUnifilar(dados.cliente))
  setTimeout(() => {
    montarDiagramaBlocos(preparado).save(nomeArquivoUnifilar(dados.cliente, 'blocos'))
  }, 400)
}
